import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Send, User, DollarSign, MessageSquare, Check, AlertCircle } from 'lucide-react'

export default function TransferModal({ onClose, user, onTransfer }) {
  const [step, setStep] = useState(1)
  const [transferData, setTransferData] = useState({
    recipient: '',
    amount: '',
    message: '',
    transferType: 'internal'
  })
  const [isProcessing, setIsProcessing] = useState(false)
  const [transferComplete, setTransferComplete] = useState(false)

  const handleInputChange = (e) => {
    setTransferData({
      ...transferData,
      [e.target.name]: e.target.value
    })
  }

  const handleNext = () => {
    if (step < 3) setStep(step + 1)
  }

  const handleBack = () => {
    if (step > 1) setStep(step - 1)
  }

  const handleTransfer = async () => {
    setIsProcessing(true)
    
    // Simulate transfer processing
    setTimeout(() => {
      setIsProcessing(false)
      setTransferComplete(true)
      
      // Call parent callback with transfer data
      if (onTransfer) {
        onTransfer({
          ...transferData,
          id: Date.now(),
          date: new Date().toISOString().split('T')[0],
          status: 'completed'
        })
      }
      
      // Auto close after 2 seconds
      setTimeout(() => {
        onClose()
      }, 2000)
    }, 2000)
  }

  const quickContacts = [
    { name: 'John Smith', email: 'john@example.com', avatar: '👨‍💼' },
    { name: 'Sarah Johnson', email: 'sarah@example.com', avatar: '👩‍💻' },
    { name: 'Mike Davis', email: 'mike@example.com', avatar: '👨‍🎨' },
    { name: 'Lisa Wilson', email: 'lisa@example.com', avatar: '👩‍🔬' }
  ]

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center md:p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="glass max-w-md w-full h-full md:h-auto md:rounded-2xl overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-4 md:p-6 border-b border-white/10">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white">Send Money</h2>
                <p className="text-sm text-gray-400">Step {step} of 3</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-4 w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(step / 3) * 100}%` }}
              />
            </div>
          </div>

          <div className="p-4 md:p-6">
            {transferComplete ? (
              /* Success Screen */
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Transfer Successful!</h3>
                <p className="text-gray-400 mb-4">
                  ${transferData.amount} sent to {transferData.recipient}
                </p>
                <p className="text-sm text-gray-500">
                  Transaction ID: TXN{Date.now().toString().slice(-8)}
                </p>
              </motion.div>
            ) : isProcessing ? (
              /* Processing Screen */
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <h3 className="text-xl font-semibold text-white mb-2">Processing Transfer...</h3>
                <p className="text-gray-400">Please wait while we process your transaction</p>
              </motion.div>
            ) : (
              /* Transfer Steps */
              <div className="space-y-6">
                {step === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h3 className="text-lg font-semibold text-white mb-4">Who do you want to send money to?</h3>
                    
                    {/* Transfer Type */}
                    <div className="mb-4">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          onClick={() => setTransferData({...transferData, transferType: 'internal'})}
                          className={`flex-1 px-4 py-2 rounded-lg transition-colors text-sm ${
                            transferData.transferType === 'internal'
                              ? 'bg-blue-600 text-white'
                              : 'bg-white/10 text-gray-400 hover:bg-white/20'
                          }`}
                        >
                          Swift Gold User
                        </button>
                        <button
                          onClick={() => setTransferData({...transferData, transferType: 'external'})}
                          className={`flex-1 px-4 py-2 rounded-lg transition-colors text-sm ${
                            transferData.transferType === 'external'
                              ? 'bg-blue-600 text-white'
                              : 'bg-white/10 text-gray-400 hover:bg-white/20'
                          }`}
                        >
                          External Bank
                        </button>
                      </div>
                    </div>

                    {/* Recipient Input */}
                    <div className="relative mb-4">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        name="recipient"
                        placeholder={transferData.transferType === 'internal' ? "Email or username" : "Account number"}
                        value={transferData.recipient}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400"
                      />
                    </div>

                    {/* Quick Contacts */}
                    <div>
                      <p className="text-gray-400 text-sm mb-3">Quick send to:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {quickContacts.map((contact, index) => (
                          <button
                            key={index}
                            onClick={() => setTransferData({...transferData, recipient: contact.email})}
                            className="p-3 bg-white/5 hover:bg-white/10 rounded-lg text-left transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              <span className="text-2xl">{contact.avatar}</span>
                              <div>
                                <p className="text-white text-sm font-medium">{contact.name}</p>
                                <p className="text-gray-400 text-xs">{contact.email}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h3 className="text-lg font-semibold text-white mb-4">How much do you want to send?</h3>
                    
                    {/* Amount Input */}
                    <div className="relative mb-4">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="number"
                        name="amount"
                        placeholder="0.00"
                        value={transferData.amount}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 text-2xl"
                      />
                    </div>

                    {/* Quick Amounts */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {[50, 100, 200].map((amount) => (
                        <button
                          key={amount}
                          onClick={() => setTransferData({...transferData, amount: amount.toString()})}
                          className="py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
                        >
                          ${amount}
                        </button>
                      ))}
                    </div>

                    {/* Message */}
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                      <textarea
                        name="message"
                        placeholder="Add a message (optional)"
                        value={transferData.message}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 resize-none"
                      />
                    </div>

                    {/* Balance Check */}
                    <div className="mt-4 p-3 bg-white/5 rounded-lg">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Available Balance:</span>
                        <span className="text-white">${user.balance.toLocaleString()}</span>
                      </div>
                      {transferData.amount && parseFloat(transferData.amount) > user.balance && (
                        <div className="flex items-center mt-2 text-red-400 text-sm">
                          <AlertCircle className="w-4 h-4 mr-2" />
                          Insufficient funds
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h3 className="text-lg font-semibold text-white mb-4">Review Transfer</h3>
                    
                    <div className="space-y-4">
                      <div className="p-4 bg-white/5 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">To:</span>
                          <span className="text-white font-medium">{transferData.recipient}</span>
                        </div>
                      </div>
                      
                      <div className="p-4 bg-white/5 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Amount:</span>
                          <span className="text-white font-semibold text-xl">${transferData.amount}</span>
                        </div>
                      </div>
                      
                      {transferData.message && (
                        <div className="p-4 bg-white/5 rounded-lg">
                          <div className="text-gray-400 text-sm mb-1">Message:</div>
                          <div className="text-white">{transferData.message}</div>
                        </div>
                      )}
                      
                      <div className="p-4 bg-white/5 rounded-lg">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-400">Transfer Fee:</span>
                          <span className="text-green-400">Free</span>
                        </div>
                        <div className="flex justify-between items-center text-sm mt-2">
                          <span className="text-gray-400">Estimated Arrival:</span>
                          <span className="text-white">Instant</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-between pt-6">
                  {step > 1 && (
                    <button
                      onClick={handleBack}
                      className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                    >
                      Back
                    </button>
                  )}
                  
                  <div className="ml-auto">
                    {step < 3 ? (
                      <button
                        onClick={handleNext}
                        disabled={
                          (step === 1 && !transferData.recipient) ||
                          (step === 2 && (!transferData.amount || parseFloat(transferData.amount) <= 0 || parseFloat(transferData.amount) > user.balance))
                        }
                        className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                      >
                        Next
                        <Send className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={handleTransfer}
                        className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
                      >
                        Send Money
                        <Send className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}